import { datadogRum } from '@datadog/browser-rum';
import { addWeeks, isBefore } from 'date-fns';
import { useAtom } from 'jotai';
import type { RequestBodyJSON } from 'openapi-typescript-helpers';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { interactionUnlockedAtState } from '@/common/atoms/interactionUnlockedAt';
import { useInteraction } from '@/common/hooks/useInteraction';
import { useInteractionParams } from '@/common/hooks/useInteractionParams';
import { FullSizeFlexContainer } from '@/components/FullSizeFlexContainer';
import { Spinner } from '@/components/Spinner';
import {
  INTERACTION_UNLOCK_DURATION_WEEKS,
  LOCKED_TERMINAL_STATUSES,
} from '@/constants/interactions';
import { GQLInteractionType } from '@/generated/graph-api';
import { coreApi } from '$api-clients/clients';
import type { CoreSchema } from '$api-clients/generated';

/** The reason value accepted by the coversheet unlock-events endpoint. */
type UnlockReason = RequestBodyJSON<
  CoreSchema.paths['/interactions/{interactionId}/coversheet/unlock-events']['post']
>['reason'];

interface InteractionLockedContextInterface {
  /**
   * The interaction is in a terminal status, so the locked experience applies
   * (the locked alert / unlock flow is shown).
   */
  isTerminal: boolean;
  /**
   * The interaction is terminal AND the user has not unlocked it this session;
   * mutating actions should be hidden or disabled.
   */
  isLocked: boolean;
  /** The interaction's current status (e.g. `expired`), used in copy. */
  status?: string;
  /** Unlock the interaction for the rest of the session, recording a reason. */
  unlock: (reason: UnlockReason, reasonOther?: string) => void;
  /** Re-lock a previously unlocked interaction. */
  lock: () => void;
}

const InteractionLockedContext =
  createContext<InteractionLockedContextInterface>({
    isTerminal: false,
    isLocked: false,
    status: undefined,
    unlock: () => undefined,
    lock: () => undefined,
  });

export function useInteractionLockedContext() {
  return useContext(InteractionLockedContext);
}

export const InteractionLockedProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { interactionId, interactionType } = useInteractionParams();

  const interactionTypeIsLockable =
    interactionType === GQLInteractionType.lease ||
    interactionType === GQLInteractionType.listing ||
    interactionType === GQLInteractionType.offer;

  const { data, isLoading } = useInteraction(
    interactionId,
    interactionType,
    !interactionTypeIsLockable || !interactionId,
  );

  const status = data?.status;

  const isTerminal = Boolean(
    interactionTypeIsLockable &&
      status &&
      LOCKED_TERMINAL_STATUSES.includes(status),
  );

  const [unlockedAt, setUnlockedAt] = useAtom(
    interactionUnlockedAtState(interactionId ?? ''),
  );

  // The unlock expires one week after it was granted, reverting to locked.
  const isUnlocked =
    unlockedAt !== null &&
    isBefore(
      Date.now(),
      addWeeks(unlockedAt, INTERACTION_UNLOCK_DURATION_WEEKS),
    );

  const isLocked = isTerminal && !isUnlocked;

  const unlock = useCallback(
    (reason: UnlockReason, reasonOther?: string) => {
      if (!interactionId || !isTerminal) return;
      // Optimistically unlock the UI while the request is in flight, then
      // re-lock it if recording the event fails.
      setUnlockedAt(Date.now());
      void coreApi
        .POST('/interactions/{interactionId}/coversheet/unlock-events', {
          params: { path: { interactionId } },
          body: { reason, additionalDetails: reasonOther },
        })
        .then(({ error }) => {
          if (error !== undefined) throw error;
        })
        .catch((unlockError: unknown) => {
          // Either an HTTP error response or a network rejection: re-lock and log.
          setUnlockedAt(null);
          datadogRum.addError(unlockError, {
            message: 'Failed to record interaction unlock event',
            interactionId,
          });
        });
    },
    [interactionId, isTerminal, setUnlockedAt],
  );

  const lock = useCallback(() => {
    setUnlockedAt(null);
  }, [setUnlockedAt]);

  // An unlock only applies to the terminal period it was granted in. Once the
  // interaction moves to a confirmed non-terminal status, clear the unlock so
  // that re-entering a terminal status locks it again. We require a loaded
  // status (not `undefined`) so a reload mid-fetch doesn't wrongly re-lock.
  const hasNonTerminalStatus =
    status !== undefined && !LOCKED_TERMINAL_STATUSES.includes(status);

  useEffect(() => {
    if (hasNonTerminalStatus && unlockedAt !== null) {
      setUnlockedAt(null);
    }
  }, [hasNonTerminalStatus, unlockedAt, setUnlockedAt]);

  const value = useMemo<InteractionLockedContextInterface>(
    () => ({ isTerminal, isLocked, status, unlock, lock }),
    [isTerminal, isLocked, status, unlock, lock],
  );

  if (isLoading) {
    return (
      <FullSizeFlexContainer>
        <Spinner fullScreen centered />
      </FullSizeFlexContainer>
    );
  }

  return (
    <InteractionLockedContext.Provider value={value}>
      {children}
    </InteractionLockedContext.Provider>
  );
};
